import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import colorHelper from 'react-color/lib/helpers/color'

import { DOMElementRect } from '@aytm/lib/common/func'

import Portal from '@aytm/lib/rfd-ui/components/basic/portal'

import { InputText } from 'primereact/inputtext'
import { ColorPicker } from 'primereact/colorpicker'

import style from '@aytm/app/stats/page/sidebar/color-options/styles/ColorPickerPopup.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

const HEX_DEBOUNCE_MS = 300

// FR-CLR-UI-43: draggable portal-rendered color picker.
// - Rendered via React Portal to escape sidebar overflow clipping
// - Draggable by its header (mousedown + document-scoped mousemove/mouseup)
// - Hex input with 300ms debounce + react-color hex validation
// - Live color preview circle inside the hex input
// - PrimeReact inline ColorPicker
// - forwardRef exposes the root DOM element for ColorLab's scroll repositioning
const ColorPickerPopup = React.forwardRef(
  ({ container, target, color, offsetTop, offsetLeft, onChange, onClose }, popupRef) => {
    const [inputValue, setInputValue] = useState(color)

    const debounceRef = useRef(null)
    const dragCleanupRef = useRef(null)
    const colorPickerRef = useRef(null)
    const colorRef = useRef(color)
    const applyRef = useRef(onChange)

    // Anchor the popup to the target swatch, clamped to container bounds.
    const positionAtTarget = useCallback(() => {
      const popup = popupRef.current
      if (!popup || !target) return

      const targetRect = DOMElementRect(target)
      const popupRect = DOMElementRect(popup)

      let top = targetRect.top + (Number.isFinite(offsetTop) ? offsetTop : targetRect.height)
      let left = targetRect.left + (Number.isFinite(offsetLeft) ? offsetLeft : targetRect.width)

      if (container) {
        const containerRect = DOMElementRect(container)
        top = Math.min(containerRect.height - popupRect.height, Math.max(0, top))
        left = Math.min(containerRect.width - popupRect.width, Math.max(0, left))
      }

      popup.style.top = `${top}px`
      popup.style.left = `${left}px`
    }, [container, target, offsetTop, offsetLeft, popupRef])

    const attachDrag = useCallback(() => {
      const popup = popupRef.current
      if (!popup) return

      const header = popup.querySelector(`.${style.header}`)
      if (!header) return

      let offsetInsideTop = 0
      let offsetInsideLeft = 0

      const onMove = event => {
        event.preventDefault()
        popup.style.top = `${event.pageY + offsetInsideTop}px`
        popup.style.left = `${event.pageX + offsetInsideLeft}px`
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      const onDown = event => {
        const rect = DOMElementRect(popup)
        offsetInsideTop = rect.top - event.pageY
        offsetInsideLeft = rect.left - event.pageX
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
      }

      header.addEventListener('mousedown', onDown)

      dragCleanupRef.current = () => {
        header.removeEventListener('mousedown', onDown)
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
    }, [popupRef])

    useEffect(() => {
      // Defer one tick so the Portal has mounted the DOM before we measure it.
      const timer = setTimeout(() => {
        positionAtTarget()
        attachDrag()
      }, 0)

      return () => {
        clearTimeout(timer)
        if (dragCleanupRef.current) dragCleanupRef.current()
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }, [positionAtTarget, attachDrag])

    const onPickerChange = event => onChange(`#${event.value}`)

    colorRef.current = color
    applyRef.current = onChange

    // The picker's own hue strip rewrites saturation and brightness to full on every pick, so a
    // colour chosen in the square is lost the moment the hue is nudged -- the marker jumps to the
    // top corner and the colour comes back fully saturated. The strip is driven here instead: the
    // pointer sets the hue, the colour already chosen keeps the other two, and because the picker
    // never enters its own drag it re-reads the value we hand back and moves its marker correctly.
    useEffect(() => {
      const strip = colorPickerRef.current?.querySelector('.p-colorpicker-hue')
      if (!strip) return

      const currentRef = { value: null }

      const pick = event => {
        const rect = strip.getBoundingClientRect()
        if (!rect.height) return

        const offset = Math.max(0, Math.min(rect.height, event.clientY - rect.top))
        const { s, v } = colorHelper.toState(currentRef.value).hsv

        applyRef.current(
          colorHelper.toState({ h: (360 * (rect.height - offset)) / rect.height, s: s || 1, v: v || 1 }).hex
        )
      }

      const stop = () => {
        document.removeEventListener('mousemove', pick)
        document.removeEventListener('mouseup', stop)
      }

      const start = event => {
        // the picker binds through React at the document root, so stopping here keeps its own
        // handler from running as well and fighting this one
        event.stopPropagation()
        event.preventDefault()
        currentRef.value = colorRef.current
        pick(event)
        document.addEventListener('mousemove', pick)
        document.addEventListener('mouseup', stop)
      }

      strip.addEventListener('mousedown', start, true)

      return () => {
        strip.removeEventListener('mousedown', start, true)
        stop()
      }
    }, [])

    // The field is seeded from the colour and then owned by whoever types in it, so a colour picked
    // in the square or the strip -- or under the popup, by another slot or a re-rolled palette --
    // has to be read back in; otherwise the field and its swatch keep announcing whatever was
    // selected when the picker opened. A pending edit is left alone, and the compare is
    // case-insensitive so the editor's upper-cased echo of the field's own commit does not rewrite
    // what was typed.
    useEffect(() => {
      if (debounceRef.current) return

      setInputValue(current => (current?.toUpperCase() === color?.toUpperCase() ? current : color))
    }, [color])

    const onInputChange = event => {
      if (debounceRef.current) clearTimeout(debounceRef.current)

      const raw = event.target.value
      const normalized = raw.startsWith('#') ? raw : `#${raw}`

      setInputValue(normalized)

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null
        if (colorHelper.isValidHex(normalized)) onChange(normalized)
      }, HEX_DEBOUNCE_MS)
    }

    return (
      <Portal>
        <div ref={popupRef} className={classNames('PRAYTMTheme', style.component)}>
          <div className={style.header}>
            Color Picker
            <div
              data-testid="stat-color-options-picker-close"
              className={style['header-close-button']}
              aria-label="Close"
              {...pressable(onClose)}
            >
              <i className="icon-aytmfd-x-v2" />
            </div>
            <span className={style['header-handle']}>
              <i className="icon-aytmfd-handle" />
            </span>
          </div>
          <div className={style.content}>
            <div className={style.row}>
              <div className={`${style.selector} p-fluid p-col-12`}>
                <InputText
                  className={classNames('p-inputtext-rounded', {
                    'p-invalid': !colorHelper.isValidHex(inputValue)
                  })}
                  aria-label="Hex color"
                  value={inputValue}
                  onChange={onInputChange}
                />
                <span
                  className={style['selector-color']}
                  style={{ backgroundColor: colorHelper.isValidHex(inputValue) ? inputValue : color }}
                />
              </div>
            </div>
            <div className={style.row}>
              <div ref={colorPickerRef} className={`${style.colorpicker} p-col-12`}>
                <ColorPicker data-testid="stat-color-options-picker" value={color} inline onChange={onPickerChange} />
              </div>
            </div>
          </div>
        </div>
      </Portal>
    )
  }
)

ColorPickerPopup.propTypes = {
  container: PropTypes.any,
  target: PropTypes.any,
  color: PropTypes.string.isRequired,
  offsetTop: PropTypes.number,
  offsetLeft: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func
}

ColorPickerPopup.displayName = 'ColorPickerPopup'

export default ColorPickerPopup
